import React, {ReactNode} from 'react';
import {EstimateTreeDto, WorkItemType} from '../../../shared/dtos/estimate-tree.dto';
import {EstimateTreeOperationDto, TreeOperationType} from '../../../shared/dtos/estimate-tree-operation.dto';
import {EstimateGateDetailsDto} from '../../../shared/dtos/estimate-gate.dto';
import {areaEntries, areaSpecFor} from '../operation-areas.service';
import {equipmentName, formatMinutes, money, rowCost} from './record-math';
import {hasSelectionList, operationFields, operationLabel, storedAnswer, totalLabel, WORK_ITEM_LABEL} from './operation-schema';
import {Photo, PhotoPager, photosOf} from './PhotoPager';
import {AllocatedScope, overUnderTag} from './job-math';
import {ActualReadout, OverUnderTag} from './JobReadouts';
import {
  AREA_LABELS, brushWoodLines, cleanupDetail, customerPlan, equipmentOrder, isCompactLoader, loaderMaterialLabel, planSections, riggingAnswer, specOf,
} from './work-plan';
import {EquipmentDto} from '../../equipment/dtos/equipment.dto';

/*
 * One work item in its saved state (WorkItemsView.kt WorkItemCard, locked):
 * the band, the photos, the site line, the measurements, each operation with
 * its answers, note and price, the Work Plan read-out, the notes and the
 * item's totals. Preview and the customer's link (SQ-337) keep the photos
 * (not the gates'), the site line, the measurements, each operation with its
 * answers and note, and the Work Plan's first section; never prices, time,
 * the equipment, the crew's sections or any of the notes below the plan.
 */

export interface RecordContext {
  /** What one labor minute is priced at: the crew's average locked rate. */
  rate?: number | null;
  crewSize: number;
  crewSelected: boolean;
  /** Wall-clock minutes, tied out: per work item and per operation. */
  itemMinutes: Map<number, number>;
  operationMinutes: Map<number, number>;
  /** Show Comparables & Generate AI Price was pressed this visit: the AI rows show. */
  aiRequested: boolean;
  /** SQ-712: the company has AI pricing. Off, no AI row, status or suggestion shows, whatever is stored. */
  aiPricing: boolean;
  /** Preview: what the customer sees. */
  customer: boolean;
  /** SQ-636: the record's prices and estimated time were hidden from this person (its read's pricesMasked). */
  pricesHidden: boolean;
  /** The company's machines: a catalog loader (Loaders category) is known by its machine, not the row's type. */
  companyEquipment?: EquipmentDto[];
  /** SQ-660: machines whose rows bill less than their minimum across the record (the job total tops them up). */
  minimumMachines?: Set<number>;
  /** SQ-660: opens the crew picker from a timer with no crew; absent where the crew can't change. */
  onSelectCrew?: () => void;
}

export const crewCaption = (n: number) => `Calculating off of ${n} crew member${n === 1 ? '' : 's'}`;

// ---- Site line and measurements ----------------------------------------------------------

/** GateWidth.label: "36 in" under 5 ft, "5 ft" / "10 ft 6 in" from there; text that is not a number stays as written. */
const gateWidthLabel = (raw?: string) => {
  if (!raw?.trim()) return undefined;
  const match = raw.match(/^\s*(\d+(?:\.\d+)?)/);
  if (!match) return raw;
  const inches = Math.round(Number(match[1]));
  if (inches === 0) return undefined;
  if (inches < 60) return `${inches} in`;
  const feet = Math.floor(inches / 12);
  const rest = inches % 12;
  return rest === 0 ? `${feet} ft` : `${feet} ft ${rest} in`;
};

/** A gate as the site line reads it: the customer's copy sends only this (SQ-337). */
export type SiteGate = Pick<EstimateGateDetailsDto, 'id' | 'width'> & {associatedTrees?: {id: number}[]};

/** "Gate Width X  •  Drag/Hauling Distance Y ft"; the customer sees it too (SQ-337). */
export const SiteLine = ({tree, gates}: {tree: EstimateTreeDto; gates: SiteGate[]}) => {
  const gate = gates.find(g => g.associatedTrees?.some(t => t.id === tree.id));
  const width = gateWidthLabel(gate?.width);
  const parts = [
    width && `Gate Width ${width}`,
    tree.dragDistanceFeet != null && `${tree.workItemType === WorkItemType.Stump ? 'Hauling' : 'Drag'} Distance ${Math.round(tree.dragDistanceFeet)} ft`,
  ].filter(Boolean);
  if (parts.length === 0) return null;
  return <div className="er-site">{parts.join('  •  ')}</div>;
};

export const detail = (tree: EstimateTreeDto, key: string) => {
  const v = (tree.additionalDetails as Record<string, unknown> | undefined)?.[key];
  return typeof v === 'string' || typeof v === 'number' ? String(v).trim() : '';
};

type Cell = {title: string; value?: string} | null;

const measurementRows = (tree: EstimateTreeDto): Cell[][] => {
  const avg = tree.workItemType === WorkItemType.TreeGroup ? 'Avg. ' : '';
  const species = {title: 'Species', value: tree.species};
  switch (tree.workItemType) {
    case WorkItemType.Misc: return [];
    case WorkItemType.Stump: return [[{title: 'Stump Diameter', value: tree.trunkDiameter}, null, species]];
    case WorkItemType.DebrisPile:
      return [[{title: 'Height', value: tree.height}, {title: 'Width', value: tree.width}, {title: 'Length', value: detail(tree, 'DebrisPile_length')}],
        [null, null, species]];
    case WorkItemType.TreeGroup:
      return [
        [{title: `${avg}Height`, value: tree.height}, {title: `${avg}Width`, value: tree.width}, {title: `${avg}Trunk Diameter`, value: tree.trunkDiameter}],
        [{title: 'Avg # Trunks', value: detail(tree, 'Tree_trunkCount')}, {title: 'Total # of Trees', value: detail(tree, 'TreeGroup_treeCount')}, species],
      ];
    default:
      return [
        [{title: 'Height', value: tree.height}, {title: 'Width', value: tree.width}, {title: 'Trunk Diameter', value: tree.trunkDiameter}],
        [{title: '# of Trunks', value: detail(tree, 'Tree_trunkCount')}, null, species],
      ];
  }
};

/** MeasurementsBlock, locked: three a row (1 : 1 : 1.35), each a caption over its value and a rule. */
const Measurements = ({tree}: {tree: EstimateTreeDto}) => {
  const rows = measurementRows(tree);
  if (rows.length === 0) return null;
  return (
    <div className="er-measurements">
      {rows.flat().map((cell, i) => cell
        ? (
          <div key={i} className="er-measure">
            <div className="er-measure-title">{cell.title}</div>
            <div className={cell.value?.trim() ? 'er-measure-value' : 'er-measure-value er-muted'}>{cell.value?.trim() || '—'}</div>
            <div className="er-measure-rule"/>
          </div>
        )
        : <div key={i}/>)}
    </div>
  );
};

// ---- Operations ------------------------------------------------------------------------------

export const SavedNote = ({label, value}: {label: string; value?: string | null}) =>
  value?.trim() ? <p className="er-note"><span className="er-muted">{label}: </span>{value.trim()}</p> : null;

/** The saved answers: pick-one rows as "question … answer", areas as their own list, details boxes as notes. */
const OperationConfig = ({op}: {op: EstimateTreeOperationDto}) => {
  const details = (op.details ?? {}) as Record<string, unknown>;
  const spec = areaSpecFor(op.operationType);
  if (spec) {
    const entries = areaEntries(spec, details as never).filter(e => e.area.trim());
    return (
      <>
        {/* SQ-660: the list is named under the operation's title, saved as in edit (OperationConfigFields.kt). */}
        <div className="er-area-title">{spec.title}</div>
        {entries.map(entry => (
          <div key={entry.area} className="er-area">
            <span className="er-area-name">{entry.area}</span>
            <span className="er-area-values">
              {spec.valueFields.map(field => entry.feet[field.key] !== undefined && (
                <span key={field.key} className="er-area-value">
                  <span className="er-area-caption">{AREA_LABELS[field.key] ?? field.label}</span>
                  <span>{entry.feet[field.key]} ft</span>
                </span>
              ))}
            </span>
          </div>
        ))}
      </>
    );
  }
  const fields = operationFields(op.operationType);
  const rows = fields.filter(f => f.kind !== 'text').map(f => {
    const value = storedAnswer(f.name, details[f.name]);
    if (f.numeric && value != null && Number(value) <= 0) return null;
    return [f.label, value === undefined ? undefined : (f.optionLabels?.[value] ?? (f.numeric ? `${value} in` : value))] as const;
  }).filter((r): r is readonly [string, string | undefined] => !!r)
    // Saved Stump Grinding reads as rows of its answers, only the answered ones.
    .filter(([, v]) => op.operationType !== TreeOperationType.Stump_Grinding || !!v);
  const texts = fields.filter(f => f.kind === 'text');
  return (
    <>
      {rows.length > 0 && (
        <div className="er-answers">
          {rows.map(([label, value]) => <div key={label} className="er-answer"><span>{label}</span><span>{value ?? ''}</span></div>)}
        </div>
      )}
      {texts.map(f => <SavedNote key={f.name} label={f.label} value={storedAnswer(f.name, details[f.name])}/>)}
    </>
  );
};

/** "AI estimating…" / "AI estimation failed" above the AI row (SignalR-fed). */
export const AiStatus = ({op}: {op: EstimateTreeOperationDto}) => {
  if (op.aiEstimatedCostGenerationStatus === 'InProgress') return <div className="er-small er-muted">AI estimating…</div>;
  if (op.aiEstimatedCostGenerationStatus === 'Failed') return <div className="er-small er-error">AI estimation failed</div>;
  return null;
};

const OperationRow = ({op, ctx, actual}: {op: EstimateTreeOperationDto; ctx: RecordContext; actual?: AllocatedScope}) => {
  const minutes = ctx.operationMinutes.get(op.id) ?? 0;
  const aiMinutes = op.aiEstimatedMinutes;
  const aiSelected = aiMinutes != null && op.estimatedMinutesOverride == null;
  const showAi = ctx.aiPricing && (ctx.aiRequested || aiSelected);
  const note = op.notes ?? '';
  const savedRows = op.operationType === TreeOperationType.Stump_Grinding;
  return (
    <div className="er-op">
      <div className="er-op-title">{operationLabel(op.operationType)}</div>
      <OperationConfig op={op}/>
      {note.trim() && (hasSelectionList(op.operationType) || savedRows) && <div className="er-op-rule er-op-rule--before"/>}
      {/* SQ-337: the operation's note is the customer's too. */}
      <SavedNote label="Note" value={note}/>
      {!ctx.customer && <>
        {/* SQ-636: an operation's time and price are not shown where they were hidden from the person. */}
        {!ctx.pricesHidden && <>
          <div className="er-op-rule"/>
          {showAi && <>
            <AiStatus op={op}/>
            <div className="er-price-row">
              <span className={aiSelected ? 'er-radio er-radio--on' : 'er-radio'} aria-hidden="true"/>
              <span className="er-price-label">AI Time &amp; Price</span>
              <span className="er-price-time">{aiMinutes != null ? formatMinutes(aiMinutes / Math.max(1, ctx.crewSize)) : '—'}</span>
              <span className="er-price-value">{money(op.aiEstimatedCost)}</span>
            </div>
          </>}
          <div className="er-price-row">
            {showAi && <span className={aiSelected ? 'er-radio' : 'er-radio er-radio--on'} aria-hidden="true"/>}
            <span className="er-price-label">Your Time &amp; Price</span>
            <span className="er-price-time">{ctx.crewSelected && (minutes > 0 || op.estimatedMinutes != null) ? formatMinutes(minutes) : '—'}</span>
            <span className="er-price-value">{money(op.estimatedCost)}</span>
          </div>
          {ctx.crewSelected && minutes > 0 && <div className="er-crew-caption">{crewCaption(ctx.crewSize)}</div>}
        </>}
        <ActualReadout layer="info" title="Estimated Time It Took (Allocated)" scope={actual}/>
      </>}
    </div>
  );
};

// ---- Work Plan read-out ---------------------------------------------------------------------------

interface PlanEntry {text: string; details?: string[]; time?: string; price?: string; minimumApplied?: boolean; notice?: string}

/**
 * SQ-660: a machine billing less than its minimum across the record. Only rows with time count toward it
 * (equipmentCharges), so a 0-minute row never carries the tag; it goes once the machine's rows pass the minimum.
 */
export const minimumApplied = (row: {equipmentId: number; durationMinutes: number}, minimumMachines?: Set<number>) =>
  row.durationMinutes > 0 && !!minimumMachines?.has(row.equipmentId);

/** SQ-660: the amber tag under a machine's time and price; the price itself is unchanged (the job total adds the top-up). */
export const MinimumAppliedTag = () => <div className="er-min-tag-row"><span className="er-min-tag">Minimum applied</span></div>;

const RATE_CHANGED = "This equipment's rate changed. This estimate keeps its rate; remove and add it again to use the new values.";

/** [pricesHidden] (SQ-636): each machine is its name alone, without its billed time, price or rate notice. */
const planEntries = (tree: EstimateTreeDto, pricesHidden: boolean, companyEquipment: EquipmentDto[] = [],
                     minimumMachines?: Set<number>): PlanEntry[] => {
  const s = planSections(tree);
  const entries: PlanEntry[] = [];
  if (s.cleanup && detail(tree, 'cleanup')) {
    const line = cleanupDetail(detail(tree, 'cleanupLocation'), detail(tree, 'cleanupNote'));
    entries.push({text: detail(tree, 'cleanup'), details: line ? [line] : []});
  }
  if (s.brushWood && detail(tree, 'brushWood')) {
    entries.push({
      text: detail(tree, 'brushWood'),
      details: brushWoodLines(detail(tree, 'brushWood'), Number(detail(tree, 'haulWoodDiameter') || 0),
        specOf(tree, 'woodSpec'), specOf(tree, 'brushSpec'), s.misc),
    });
  }
  const removal = detail(tree, 'removalStrategy');
  if (s.removal && removal) entries.push({text: removal});
  if (s.treeWork) {
    if (detail(tree, 'reachMethod')) entries.push({text: detail(tree, 'reachMethod')});
    if (detail(tree, 'debrisMethod')) entries.push({text: `Debris ${detail(tree, 'debrisMethod')}`});
    if (detail(tree, 'riggingAmount')) entries.push({text: riggingAnswer(detail(tree, 'riggingAmount'))});
    if (s.remainingStem && detail(tree, 'remainingStem')) entries.push({text: detail(tree, 'remainingStem')});
  }
  equipmentOrder(tree, tree.equipment ?? []).forEach(row => {
    const loader = isCompactLoader(companyEquipment.find(e => e.id === row.equipmentId), row);
    entries.push({
      text: equipmentName(row),
      details: loader ? [
        detail(tree, 'loaderMaterial') && `Hauling ${loaderMaterialLabel(tree, detail(tree, 'loaderMaterial'))}`,
        detail(tree, 'loaderAccess') && `${detail(tree, 'loaderAccess')} with Machine`,
      ].filter((l): l is string => !!l) : [],
      ...(pricesHidden ? {} : {
        time: formatMinutes(row.durationMinutes),
        price: row.hourlyRate == null ? '—' : money(rowCost(row)),
        minimumApplied: minimumApplied(row, minimumMachines),
        notice: row.rateChanged ? RATE_CHANGED : undefined,
      }),
    });
  });
  return entries;
};

const WorkPlan = ({tree, pricesHidden, companyEquipment, minimumMachines}: {
  tree: EstimateTreeDto; pricesHidden: boolean; companyEquipment?: EquipmentDto[]; minimumMachines?: Set<number>;
}) => {
  if (tree.operations.length === 0) return null;
  const entries = planEntries(tree, pricesHidden, companyEquipment, minimumMachines);
  return (
    <div className="er-plan">
      <div className="er-plan-head">Work Plan</div>
      {entries.length === 0 && <div className="er-plan-empty">Nothing selected yet.</div>}
      {entries.map((entry, i) => (
        <React.Fragment key={i}>
          {i > 0 && <div className="er-plan-rule"/>}
          <div className="er-plan-entry">
            <div className="er-plan-row">
              <span className="er-plan-text">{entry.text}</span>
              {entry.time && <span className="er-plan-num">{entry.time}</span>}
              {entry.price && <span className="er-plan-num">{entry.price}</span>}
            </div>
            {entry.minimumApplied && <MinimumAppliedTag/>}
            {entry.details?.map(line => <div key={line} className="er-plan-detail">{line}</div>)}
            {entry.notice && <div className="er-notice">{entry.notice}</div>}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

/**
 * The customer's Work Plan (SQ-337): its first section alone, as answered,
 * and nothing at all while it is unanswered (no "Nothing selected yet").
 */
const CustomerWorkPlan = ({tree}: {tree: EstimateTreeDto}) => {
  const plan = customerPlan(tree);
  if (!plan) return null;
  return (
    <div className="er-plan">
      <div className="er-plan-head">Work Plan</div>
      <div className="er-plan-entry">
        <div className="er-plan-section">{plan.title}</div>
        <div className="er-plan-row"><span className="er-plan-text">{plan.answer}</span></div>
        {plan.details.map(line => <div key={line} className="er-plan-detail">{line}</div>)}
      </div>
    </div>
  );
};

// ---- The card -------------------------------------------------------------------------------------------

export const workItemTitle = (tree: EstimateTreeDto, number: number) => `#${number} ${WORK_ITEM_LABEL[tree.workItemType] ?? tree.workItemType}`;

/**
 * A job's additions (SQ-597). [review]: the job is being finished: the Work
 * Plan is the answerable one, the equipment's over/under shows from the time
 * each machine actually ran, and the tan time card sits under the total.
 * [completed]: what the time came to and whether it was bid right, at every
 * operation and at the work item.
 */
export interface WorkItemJob {
  review?: {workPlan: ReactNode; allocation: ReactNode};
  completed?: {byOperation: Map<number, AllocatedScope>; item?: AllocatedScope};
  /** Equipment over (+) / under (−) its billed time, for the Equipment Cost tag. */
  equipmentOver?: number;
}

const WorkItemCard = ({tree, number, gates, ctx, onOpenPhoto, cardRef, job}: {
  tree: EstimateTreeDto; number: number; gates: SiteGate[]; ctx: RecordContext;
  onOpenPhoto: (photo: Photo) => void;
  cardRef?: (el: HTMLElement | null) => void;
  job?: WorkItemJob;
}) => {
  const photos = photosOf(tree, ctx.customer ? [] : gates);
  const equipment = tree.equipment ?? [];
  const equipmentCost = equipment.reduce((sum, row) => sum + rowCost(row), 0);
  const laborCost = tree.operations.reduce((sum, o) => sum + (o.estimatedCost ?? 0), 0);
  const minutes = ctx.itemMinutes.get(tree.id) ?? 0;
  const noteBelowPlan = [tree.notes, detail(tree, 'debrisManagement'), tree.crewNotes].some(n => n?.trim());
  return (
    <article className="er-item" ref={cardRef}>
      <header className="er-item-band">{workItemTitle(tree, number)}</header>
      <div className="er-item-body">
        <PhotoPager photos={photos} onOpen={onOpenPhoto}/>
        <SiteLine tree={tree} gates={gates}/>
        <Measurements tree={tree}/>
        {tree.operations.length === 0
          ? <div className="er-small er-muted er-none">No operations yet.</div>
          : tree.operations.map(op => <OperationRow key={op.id} op={op} ctx={ctx} actual={job?.completed?.byOperation.get(op.id)}/>)}
        {ctx.customer ? <CustomerWorkPlan tree={tree}/> : job?.review
          ? job.review.workPlan
          : <WorkPlan tree={tree} pricesHidden={ctx.pricesHidden} companyEquipment={ctx.companyEquipment}
                      minimumMachines={ctx.minimumMachines}/>}
        {/* SQ-636: with the totals hidden and no note, the item ends at its Work Plan: no rule under it. */}
        {!ctx.customer && (!ctx.pricesHidden || noteBelowPlan) && <div className="er-item-rule"/>}
        {!ctx.customer && <SavedNote label="Work Item Note" value={tree.notes}/>}
        {!ctx.customer && <SavedNote label="Debris Management" value={detail(tree, 'debrisManagement')}/>}
        {!ctx.customer && <SavedNote label="Crew Note" value={tree.crewNotes}/>}
        {/* SQ-636: no totals where the prices were hidden from the person (nor a split: the actual times go with them). */}
        {!ctx.customer && !ctx.pricesHidden && <>
          <div className="er-item-rule er-item-rule--totals"/>
          <div className="er-total-row">
            <span>Equipment Cost</span>
            {job?.equipmentOver !== undefined && <OverUnderTag text={overUnderTag(job.equipmentOver)} over={job.equipmentOver > 0}/>}
            <span className="er-total-value">{money(equipmentCost)}</span>
          </div>
          <div className="er-total-row">
            <span>Labor Time &amp; Cost</span>
            <span className="er-total-value">{ctx.crewSelected ? formatMinutes(minutes) : '—'}</span>
            <span className="er-total-value">{money(laborCost)}</span>
          </div>
          {ctx.crewSelected && minutes > 0 && <div className="er-crew-caption">{crewCaption(ctx.crewSize)}</div>}
          <div className="er-total-row er-total-row--strong">
            <span>{totalLabel(tree.workItemType)}</span>
            <span className="er-total-value">{money(equipmentCost + laborCost)}</span>
          </div>
          {job?.review?.allocation}
          {job?.completed && <ActualReadout layer="item" title="Estimated Time It Took (Allocated)" scope={job.completed.item}/>}
        </>}
      </div>
    </article>
  );
};

export default WorkItemCard;
